import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { pinia } from '@/pinia';
import { hiddenRoutes } from '@/router/hidden.router';
import { useOrganizationStore } from '@/stores/organzation.pinia';

import { EnumRoutes } from './routes.enum';

const orgStore = useOrganizationStore(pinia);
const { dashboardText, coursesText, internalTeamMode } = storeToRefs(orgStore);

/**
 * Nav items to HIDE when the org's `internal-team-mode` flag is ON. This is
 * NAV-ONLY hiding — the underlying routes stay registered and reachable by
 * URL (deep links, in-app CTAs like the Journey Resume button). Route-level
 * blocking is a separate mechanism (`hiddenInInternalTeamMode` meta,
 * permissoins.store) reserved for the marketplace surfaces.
 *
 * Items are identified by route-enum name so the filter survives CMS label
 * changes:
 * - "Explore Courses" -> externalCourses.root (KP-20) — the partner/external
 *   course catalog.
 * - "Schedule"        -> social.events (KP-20) — the events/activities schedule.
 * - "Dashboard"       -> dashboard.root (KP-68) — superseded by the hub; the
 *   consolidated entry below reuses its label (dashboardText) and icon.
 * - "My Modules"      -> lms.root (KP-68) — now the hub's "My Modules" tab
 *   (InternalTeamPage.vue).
 * - "Journey"         -> journeyRoutesEnum.root (KP-68) — the hub's Journey tab.
 * - "Library"         -> libraryRoutesEnum.root (KP-68) — the hub's Library tab.
 */
export const internalTeamHiddenItemNames = [
  EnumRoutes.externalCourses.root.name,
  EnumRoutes.social.events.name,
  EnumRoutes.dashboard.root.name,
  EnumRoutes.lms.root.name,
  EnumRoutes.journeyRoutesEnum.root.name,
  EnumRoutes.libraryRoutesEnum.root.name,
];

const baseHeaderItems = computed(() => {
  const items = [
    // The internal-team hub first: it is the default landing when the
    // `internal-team-mode` org flag is enabled (see auth.store
    // handleSetLoginData for the explicit landing switch) and, since KP-68,
    // the ONLY flagged nav entry (retitled to the org's dashboard
    // terminology below). With the flag off the item is filtered out by its
    // shouldHaveInternalTeamMode config gate (permissoins.store
    // checkAuthentication) and the legacy dashboard stays first in the
    // rendered nav.
    EnumRoutes.internalTeamRoutesEnum.root,
    { ...EnumRoutes.dashboard.root, title: dashboardText.value },
    { ...EnumRoutes.lms.root, title: coursesText.value },
    { ...EnumRoutes.externalCourses.root },
    EnumRoutes.social.events,
    EnumRoutes.social.socialGroups,
    EnumRoutes.journeyRoutesEnum.root,
    // EnumRoutes.mobile.downloadTheApp,
    EnumRoutes.libraryRoutesEnum.root,
    // EnumRoutes.chatRoutesEnum.root,
  ];

  // When the flag is OFF the nav must be byte-identical to today (zero regression).
  if (!internalTeamMode.value) return items;

  // KP-68: consolidate the flagged nav to ONE entry — the Internal Training
  // item retitled to the org's dashboard terminology (لوحة التحكم /
  // "Dashboard") with the dashboard icon, opening the tabbed hub
  // (/internal-team), mirroring the admin-side consolidation. Everything the
  // removed entries pointed at lives on as a hub tab. The old "New" badge is
  // intentionally dropped (owner decision).
  return items
    .filter((item) => !internalTeamHiddenItemNames.includes(item.name))
    .map((item) => {
      if (item.name !== EnumRoutes.internalTeamRoutesEnum.root.name)
        return item;
      return {
        ...item,
        // dashboardText may be '' before the terminology request resolves;
        // the seeded i18n key `header.dashboard` (plugins/i18n.ts, en+ar —
        // overwritten by the runtime CMS bundle when it carries the key)
        // renders as the fallback. dontUseKey mirrors the legacy Dashboard
        // entry: a literal CMS label must bypass t(); the key must not.
        title: dashboardText.value || 'header.dashboard',
        icon: EnumRoutes.dashboard.root.icon,
        meta: { ...item.meta, dontUseKey: Boolean(dashboardText.value) },
      };
    });
});
const headerItemsUnAuth = computed(() => [
  ...baseHeaderItems.value,
  EnumRoutes.landing.about,
  EnumRoutes.landing.contactUs,
]);
const headerProfileItems = computed(() => [
  EnumRoutes.profile.activityLog.root,
  EnumRoutes.profile.savedPost.root,
  EnumRoutes.financials.myPurchases,
  EnumRoutes.profile.myCertificates.root,
  EnumRoutes.dashboard.userActivities,
]);
export const EnumHeaderList = computed(() => ({
  headerItemsAuth: [
    ...baseHeaderItems.value.filter(
      (item) => !hiddenRoutes.includes(item.name)
    ),
  ],
  headerItemsUnAuth: headerItemsUnAuth.value.filter(
    (item) => !hiddenRoutes.includes(item.name)
  ),
  headerProfileItems: headerProfileItems.value.filter(
    (item) => !hiddenRoutes.includes(item.name)
  ),
}));
